import i18n from 'i18next';
import { logTrace, logError } from './loggerService';
import { showErrorModal } from './errorModalService';
import { useAuthStore } from '../store/useAuthStore';
import { isSupportedLanguage } from '../i18n/config';
import { CSRF_COOKIE_NAME, SESSION_MODE } from './sessionCookieNames';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const X_API_KEY = import.meta.env.VITE_X_API_KEY || '';

export const isCookieMode = SESSION_MODE === 'cookie';

// Regex over `document.cookie` to extract the CSRF cookie value the FE
// echoes back in X-CSRF-Token. Built at module load from the env-configured
// cookie name so admin reads `csrf_admin_token` even when the parent-domain
// consumer `csrf_token` is also present in the cookie jar.
const csrfCookieValueRegex = new RegExp(
  `(?:^|;\\s*)${CSRF_COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`,
);

// One-time cleanup of a stale host-only CSRF cookie left over from the
// Firebase-Hosting-proxy era (when the SPA called /api on its own origin and
// browsers scoped the response cookie to the app subdomain). It now shadows
// the new Domain-scoped cookie when JS reads document.cookie. Setting it
// without a Domain attribute targets only the host-only cookie on this host;
// the Domain=staging.homeplated.com cookie is unaffected.
//
// Skipped on local dev (MODE=development): the local backend has no
// cookieDomain, so the only CSRF cookie IS host-only — running this
// would delete the legit cookie.
if (isCookieMode && typeof document !== 'undefined' && ['dev', 'stg', 'prod'].includes(import.meta.env.MODE)) {
  document.cookie = `${CSRF_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}

// State that persists as long as the user is logged in (localStorage mode only)
let jwt: string | null = null;
let activeProfileId: string | null = null;

// Token refresh state
let tokenRefreshHandler: (() => Promise<boolean>) | null = null;
let refreshInProgress: Promise<RefreshResult> | null = null;

// Single-shot guard: a burst of failing requests should trigger one logout,
// not N. Dynamic import of authService avoids the circular dep (authService
// statically imports apiPost/apiPut/apiGet from this module).
let logoutInProgress = false;

async function triggerAuthFailureLogout(): Promise<void> {
  if (logoutInProgress) return;
  logoutInProgress = true;
  try {
    const { logout } = await import('./authService');
    await logout();
  } catch {
    // logout() ends with window.location.assign('/'); if anything before that
    // throws hard, still navigate so the user lands somewhere usable — UNLESS
    // we're inside the share-arrival grace window (see ShareRedirectPage +
    // authStorageKeys), in which case redirecting blows the visitor out to
    // home mid-share-flow. Same guard as logout() itself.
    if (typeof window !== 'undefined') {
      const { isWithinShareArrivalGrace } = await import('./authStorageKeys');
      if (!isWithinShareArrivalGrace()) {
        window.location.assign('/');
      }
    }
  }
}

/**
 * In-memory logout — no page reload. Used when the BE tells us there was
 * never a credential on the request (`NO_ACCESS_TOKEN` / `NO_REFRESH_TOKEN`).
 * Reloading in that case would re-read a leaked sibling-env CSRF cookie and
 * loop. The React tree reacts to `isAuthenticated` flipping false and the
 * router renders the Sign In shell in place.
 */
async function triggerAuthFailureSoftLogout(): Promise<void> {
  if (logoutInProgress) return;
  logoutInProgress = true;
  try {
    const { logoutSoft } = await import('./authService');
    await logoutSoft();
  } catch {
    // Best-effort — even if soft logout throws, in-memory store cleared
    // by useAuthStore consumers + the thrown error from the original
    // request lands the user on the right state.
  }
}

type RefreshResult = 'ok' | 'no_refresh' | 'failed';

export function setJWT(token: string | null) {
  if (!isCookieMode) jwt = token;
}

export function setActiveProfileId(profileId: string | null) {
  activeProfileId = profileId;
}

export function clearAuthState() {
  jwt = null;
  activeProfileId = null;
}

export function setTokenRefreshHandler(handler: (() => Promise<boolean>) | null) {
  tokenRefreshHandler = handler;
}

export async function attemptTokenRefresh(): Promise<RefreshResult> {
  if (isCookieMode) {
    return attemptCookieRefresh();
  }

  if (!tokenRefreshHandler) return 'failed';

  if (refreshInProgress) {
    return refreshInProgress;
  }

  refreshInProgress = tokenRefreshHandler()
    .then((ok): RefreshResult => (ok ? 'ok' : 'failed'))
    .finally(() => {
      refreshInProgress = null;
    });

  return refreshInProgress;
}

// Cookie mode: call the refresh endpoint - browser sends refresh cookie automatically.
// Returns `'no_refresh'` when the BE says the refresh cookie wasn't even sent
// (cross-env-leak case: caller skips reload to avoid the loop).
async function attemptCookieRefresh(): Promise<RefreshResult> {
  if (refreshInProgress) {
    return refreshInProgress;
  }

  refreshInProgress = (async (): Promise<RefreshResult> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Mode': 'cookie',
          'X-CSRF-Token': getCsrfToken(),
          ...(X_API_KEY ? { 'X-Api-Key': X_API_KEY } : {}),
        },
      });
      if (response.ok) return 'ok';
      try {
        const body = await response.clone().text();
        if (JSON.parse(body)?.error?.code === 'NO_REFRESH_TOKEN') {
          return 'no_refresh';
        }
      } catch { /* non-JSON / no code */ }
      return 'failed';
    } catch {
      return 'failed';
    }
  })().finally(() => {
    refreshInProgress = null;
  });

  return refreshInProgress;
}

export function getJWT(): string | null {
  return jwt;
}

export function getActiveProfileId(): string | null {
  return activeProfileId;
}

// Read the per-app CSRF token from `document.cookie` (set by BE on login,
// NOT HttpOnly). Cookie name is `CSRF_COOKIE_NAME` — `csrf_token` for the
// consumer family (default), `csrf_admin_token` for admin via its env
// override. Echoed back in `X-CSRF-Token` so the BE's content-driven
// session-pick can identify which family the request belongs to.
function getCsrfToken(): string {
  const match = document.cookie.match(csrfCookieValueRegex);
  return match ? decodeURIComponent(match[1]) : '';
}

// translateBackendErrorCode pulls the coded-error shape (per
// `api-core-go/docs/i18n.md` §10) off a parsed error response body and
// returns a localized user-facing string. Two fallback levels handle gaps:
//
//   1. Locale fallback (i18next `fallbackLng: 'en'`) — if the user's locale
//      doesn't have the key yet (translator gap), serves `en`.
//   2. Key fallback (defaultValue) — if even `en` doesn't have the key
//      (BE shipped a new code the FE bundle predates), serves
//      `errors:UNKNOWN` ("Something went wrong").
//
// Returns null when the response carries no `error.code` — caller falls
// back to the legacy `message` field or the raw dev-facing string. Params
// are spread into the template so codes like ORDER_CANCELED interpolate
// `{{orderNumber}}` correctly.
function translateBackendErrorCode(errorJson: unknown): string | undefined {
  if (!errorJson || typeof errorJson !== 'object') return undefined;
  const errBlock = (errorJson as { error?: unknown }).error;
  if (!errBlock || typeof errBlock !== 'object') return undefined;
  const code = (errBlock as { code?: unknown }).code;
  if (typeof code !== 'string' || code.length === 0) return undefined;
  const params = ((errBlock as { params?: unknown }).params ?? {}) as Record<string, unknown>;
  const fallback = i18n.t('errors:UNKNOWN');
  return i18n.t(`errors:${code}`, { ...params, defaultValue: fallback }) as string;
}

// Attach `?l=<code>` to outbound URLs so the BE middleware can populate
// `ctxData.Language` for server-side localization (notification copy,
// activity messages, AI prompts). Belt-and-suspenders with the
// Accept-Language header buildHeaders sets — the BE prefers `?l=` so
// query param wins, header is fallback.
//
// No-ops when:
//  - i18n.language isn't in SUPPORTED_LANGUAGES (e.g. bootstrap before
//    detection has settled — the BE will fall through to its own default).
//  - the URL already has an explicit `l=` param (caller knows better,
//    e.g. a deeplink-handling endpoint that intentionally overrides).
function withLangParam(rawUrl: string): string {
  try {
    const lang = i18n.language;
    if (!isSupportedLanguage(lang)) return rawUrl;
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const u = new URL(rawUrl, base);
    if (!u.searchParams.has('l')) {
      u.searchParams.set('l', lang);
    }
    return u.toString();
  } catch {
    // Don't ever fail a request because we couldn't decorate the URL.
    return rawUrl;
  }
}

// Registered by App.tsx so we can do client-side routing into the no-network
// page without a full page reload — important because a full reload while
// actually offline can't fetch the SPA shell (no service worker in dev).
let noNetworkNavigator: (() => void) | null = null;

export function setNoNetworkNavigator(fn: (() => void) | null) {
  noNetworkNavigator = fn;
}

// Fetch throws (TypeError) when the request can't reach the server — captive
// portal, airplane mode, dead Wi-Fi, etc. Surface the no-network recovery
// page once, and avoid looping if we're already on it.
function handleNetworkFailure() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/no-network') return;
  if (noNetworkNavigator) {
    noNetworkNavigator();
  } else {
    window.location.assign('/no-network');
  }
}

// Registered by App.tsx, mirroring the no-network navigator: route into the
// service-unavailable page on a client-side transition (no full reload).
let serviceUnavailableNavigator: (() => void) | null = null;

export function setServiceUnavailableNavigator(fn: (() => void) | null) {
  serviceUnavailableNavigator = fn;
}

// The BE returns 503 when it started without a reachable database (degraded
// mode) — e.g. staging's DB is stopped to save cost, or a real outage. Show
// the full-screen "temporarily unavailable" recovery page once, avoiding a
// loop if we're already on it.
function handleServiceUnavailable() {
  serviceUnavailableActive = true;
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/service-unavailable') return;
  if (serviceUnavailableNavigator) {
    serviceUnavailableNavigator();
  } else {
    window.location.assign('/service-unavailable');
  }
}

// Latch set once handleServiceUnavailable fires. While active, maybeShowErrorModal
// no-ops so we don't stack an "Error: something went wrong" modal on top of the
// full-screen unavailable splash. Cleared by a page reload (the splash's "Try
// again" button calls window.location.reload → fresh module instance → false).
// Rationale: during a BE outage several concurrent requests may resolve with
// mixed statuses — the first 503 fires the splash; a straggler 500 (e.g. LB
// timeout, a /ping that reaches a broken pool) would otherwise trigger the
// generic modal path and stack on top.
let serviceUnavailableActive = false;

function maybeShowErrorModal(message: string, correlationId?: string, errorCode?: string) {
  if (serviceUnavailableActive) return;
  showErrorModal(message, correlationId, errorCode);
}

function buildHeaders(includeContentType = true, serviceKey?: string, method?: string): HeadersInit {
  const headers: Record<string, string> = {};

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (serviceKey) {
    // A service key is an explicit credential, not a user session — send it as
    // a Bearer token regardless of session mode (in cookie mode the user
    // session still rides on cookies; a logged-out service-key call like the
    // public AI-generate path has no cookie and relies on this header).
    headers['Authorization'] = `Bearer ${serviceKey}`;
  } else if (isCookieMode) {
    // Cookie mode: no Authorization header, browser sends cookies
    headers['X-Session-Mode'] = 'cookie';

    // CSRF token is sent on EVERY request — not just state-changing methods.
    // CSRF protection only fires server-side on non-safe methods. Which
    // session (consumer or admin cookies) a request uses is decided by the
    // BE from its Origin; this header only selects the admin session when it
    // carries admin's CSRF token, which this app never sends.
    const csrf = getCsrfToken();
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }
  } else if (jwt) {
    // localStorage mode: attach JWT as Bearer token
    headers['Authorization'] = `Bearer ${jwt}`;
  }

  if (X_API_KEY) {
    headers['X-Api-Key'] = X_API_KEY;
  }

  if (activeProfileId) {
    headers['X-Sub-Identity'] = activeProfileId;
  }

  // Accept-Language is a secondary lang signal for the BE middleware —
  // primary is the `?l=` query param attached via withLangParam. Sending
  // both means the BE can use either; we don't have to know which lookup
  // it actually reads. Skip when i18n hasn't settled on a supported lang
  // yet so we don't send a header the BE will ignore anyway.
  const lang = i18n.language;
  if (isSupportedLanguage(lang)) {
    headers['Accept-Language'] = lang;
  }

  return headers;
}

/**
 * Per-call request options. `quiet404`: a 404 throws as usual but skips the
 * global error modal — for background fetches whose caller already handles
 * "it's gone" (e.g. an activity card for a deleted order or listing).
 * `quietStaleFetch`: same for a 409 STALE_FETCH, for a caller that resends on
 * it (the publish form's listing save queue).
 */
export interface RequestOpts {
  quiet404?: boolean;
  quietStaleFetch?: boolean;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false,
  serviceKey?: string,
  opts: RequestOpts = {}
): Promise<T> {
  const url = withLangParam(`${API_BASE_URL}${endpoint}`);
  const method = options.method || 'GET';

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      ...(isCookieMode ? { credentials: 'include' as RequestCredentials } : {}),
      headers: {
        ...buildHeaders(true, serviceKey, method),
        ...options.headers,
      },
    });
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') {
      handleNetworkFailure();
    }
    throw err;
  }

  if (!response.ok) {
    // 503: the BE is in degraded mode (no DB) — every data route returns this.
    // Short-circuit to the full-screen unavailable page before any
    // refresh/retry/error-modal logic; there's nothing to recover per-request.
    if (response.status === 503) {
      handleServiceUnavailable();
      const err = new Error(`API Error 503: service temporarily unavailable`) as Error & {
        status?: number;
        errorCode?: string;
      };
      err.status = 503;
      err.errorCode = 'SERVICE_UNAVAILABLE';
      throw err;
    }

    // CSRF mismatch: BE rejected the X-CSRF-Token header (header missing,
    // cookie missing, or header/cookie don't match). POST /auth/refresh
    // rotates the csrf_token cookie as a side effect, so refresh + retry
    // recovers transparently. Exclude /auth/refresh and /auth/logout to
    // prevent recursion if those endpoints themselves return CSRF_INVALID.
    if (
      response.status === 403 &&
      !isRetry &&
      !serviceKey &&
      !endpoint.includes('/auth/refresh') &&
      !endpoint.includes('/auth/logout')
    ) {
      const peek = await response.clone().text();
      let isCsrfInvalid = false;
      try {
        isCsrfInvalid = JSON.parse(peek)?.error?.code === 'CSRF_INVALID';
      } catch { /* not JSON — not a coded CSRF error */ }

      if (isCsrfInvalid) {
        const refreshed = await attemptTokenRefresh();
        if (refreshed === 'ok') {
          return request<T>(endpoint, options, true, serviceKey, opts);
        }
        if (refreshed === 'no_refresh') {
          await triggerAuthFailureSoftLogout();
        } else {
          await triggerAuthFailureLogout();
        }
        throw new Error(`API Error 403: CSRF_INVALID, refresh failed`);
      }
    }

    if (response.status === 401 && !isRetry && !serviceKey && !endpoint.startsWith('/login') && !endpoint.includes('/auth/refresh')) {
      // Always attempt refresh on 401 — the BE's specific 401 reason
      // (NO_ACCESS_TOKEN, ACCESS_TOKEN_INVALID, etc.) is debug noise here.
      // The authoritative answer for "is this session recoverable?" is the
      // refresh result, not the original 401 code:
      //   - 30-min access cookie expiry → access cookie absent → BE says
      //     NO_ACCESS_TOKEN → refresh succeeds (refresh cookie has 30d TTL)
      //     → retry transparently.
      //   - Cross-env-leak (CSRF cookie leaked from a sibling env, no real
      //     session here) → no refresh cookie either → refresh returns
      //     'no_refresh' → soft-logout (no reload, sentinel can't re-trigger).
      //   - Real session expiry (refresh JWT bad or expired) → refresh
      //     returns 'failed' → hard-logout (reload to Sign In).
      const refreshed = await attemptTokenRefresh();
      if (refreshed === 'ok') {
        return request<T>(endpoint, options, true, serviceKey, opts);
      }
      // Refresh failed — session is gone. Only act for authenticated users;
      // a 401 from a pre-auth flow (e.g. login-code) should propagate to
      // the caller for friendly handling.
      if (useAuthStore.getState().isAuthenticated) {
        if (refreshed === 'no_refresh') {
          await triggerAuthFailureSoftLogout();
        } else {
          await triggerAuthFailureLogout();
        }
        throw new Error(`API Error 401: refresh failed`);
      }
    }

    const errorBody = await response.text();

    let correlationId: string | undefined;
    let userMessage: string | undefined;
    let errorCode: string | undefined;
    try {
      const errorJson = JSON.parse(errorBody);
      correlationId = errorJson.correlationId || errorJson.CorrelationId;
      errorCode = errorJson?.error?.code;
      // Prefer the new coded shape ({error: {code, params}}) when the BE
      // emitted it — translates via i18next so the user sees their
      // language. Falls back to errors:UNKNOWN inside the helper if the
      // code isn't in the FE bundle. Un-coded legacy responses come
      // through with errorJson.message as the BE's English prose.
      userMessage =
        translateBackendErrorCode(errorJson) ??
        (typeof errorJson.message === 'string' ? errorJson.message : undefined);
    } catch {
      // Not JSON or no correlationId
    }

    const error = new Error(`API Error ${response.status}: ${errorBody}`);
    // Recursion guard: logError itself PUTs to /log. If the failing request
    // WAS /log, calling logError here would try to log the failure to the
    // same endpoint, fail the same way, and loop forever. Fall back to
    // console for that one case so the FE stays quiet if the BE rejects
    // our log payload (e.g. missing/invalid fields).
    if (endpoint !== '/log') {
      logError(error, `${method} ${endpoint} failed`, undefined, {
        status: response.status,
        endpoint,
        body: errorBody,
        correlationId,
      });
    } else {
      console.error(`${method} ${endpoint} failed`, response.status, errorBody);
    }

    // Attach status + coded-error metadata so callers that handle errors
    // inline (excluded endpoints below) can read the translated message
    // and code without re-parsing the body. Non-excluded callers get the
    // global modal AND these fields — both paths are useful.
    const enriched = error as Error & {
      status?: number;
      userMessage?: string;
      errorCode?: string;
      correlationId?: string;
    };
    enriched.status = response.status;
    if (userMessage) enriched.userMessage = userMessage;
    if (errorCode) enriched.errorCode = errorCode;
    if (correlationId) enriched.correlationId = correlationId;

    if (response.status === 404 && opts.quiet404) {
      // The caller asked to handle a missing record itself (RequestOpts).
    } else if (response.status === 409 && errorCode === 'STALE_FETCH' && opts.quietStaleFetch) {
      // The caller resends on a stale-version refusal (RequestOpts).
    } else if (response.status === 401) {
      // No-op: authenticated 401s already triggered logout+reload above;
      // pre-auth 401s propagate to the caller. Kept in the if/else-if chain
      // so 401s never fall into the global error-modal branch.
    } else if (response.status === 410 && endpoint === '/login-code') {
      // Code expired — let the caller handle this without a global error modal
    } else if (response.status === 409 && endpoint.startsWith('/access-code/')) {
      // Duplicate email/phone on another user during contact-change — surface inline
    } else if (response.status === 409 && endpoint.startsWith('/profile/')) {
      // Duplicate email/phone on profile PATCH — surface inline on the edit modal
    } else if (response.status === 400 && endpoint === '/stripe/promo/validate') {
      // Invalid promo code — let the caller handle this without a global error modal
    } else if (response.status === 404 && endpoint.startsWith('/listings/local/search')) {
      // Not in a dish locale — let the caller handle this without a global error modal
    } else if (response.status === 400 && endpoint === '/orders/reserve') {
      // Portions changed under the user (concurrent order drained availability) —
      // let the caller surface a friendly "portions updated" prompt instead.
    } else if (
      response.status === 403 &&
      errorCode === 'STATUS_INVALID' &&
      method === 'POST' &&
      /^\/orders\/[^/]+\/(verify|deny-handoff)$/.test(endpoint)
    ) {
      // The order already left `ready_to_pickup` under a stale screen: a second
      // tap after the confirm completed it, or the chef canceled / no-showed
      // while the socket missed the event. There's nothing for the user to act
      // on, so useVerifyOrder / useDenyHandoff refetch and the stale ball or
      // OrderConfirmGate drops on its own. A modal here would sit on top of a
      // gate that keeps rejecting every tap.
    } else if (
      response.status === 400 &&
      (endpoint === '/locale' || endpoint.startsWith('/locale?'))
    ) {
      // US-only / locale resolve errors — surface inline in the location
      // picker modal (useLocaleCapture::setSearchError/setGeoError). Showing
      // a global modal ON TOP of an open location modal is jarring.
      // Match both `/locale` (no params) and `/locale?...` because
      // localeService builds the URL with a query string (lat/lon and/or
      // postalCode) — strict equality alone misses the real call sites.
    } else if (response.status === 404 && endpoint.startsWith('/locale/')) {
      // /locale/{id} — the cached `profile.currentLocaleId` points to a
      // locale row that's been deleted/soft-deleted. Auth-restore handles
      // this by clearing the cached locale so the user lands on the
      // in-feed location prompt. A modal popping mid-restore would be
      // user-hostile (they just signed in, nothing they did is broken).
    } else if (
      response.status === 400 &&
      (errorCode === 'COUNTRY_NOT_SUPPORTED' || errorCode === 'LISTING_PRICE_OVER_LOCALE_CAP') &&
      (method === 'POST' || method === 'PATCH') &&
      endpoint.startsWith('/listings')
    ) {
      // Location-driven publish rejections — surfaced inline by
      // PublishListingPage (near LocationPicker / the price row) rather than
      // in a global modal. FE pre-validation should catch most cases:
      //   COUNTRY_NOT_SUPPORTED       — publishBlockers.usOnly
      //   LISTING_PRICE_OVER_LOCALE_CAP — the price wheel's locale cap
      // These exclusions cover the defense-in-depth paths (e.g. publishing
      // before the new location's locale, and therefore its cap, resolved).
    } else {
      // User-facing message: the translated coded-error wins, then the
      // BE's legacy `message` prose, then the raw dev-facing
      // "API Error 500: ..." string as a last resort. maybeShowErrorModal
      // no-ops if the service-unavailable splash is already up.
      maybeShowErrorModal(userMessage ?? error.message, correlationId, errorCode);
    }

    throw error;
  }

  const text = await response.text();

  if (response.status === 204 || !text) {
    logTrace(`${method} ${endpoint} completed`, undefined, { status: response.status });
    return undefined as T;
  }

  const data = JSON.parse(text);
  logTrace(`${method} ${endpoint} completed`, undefined, { status: response.status, data });
  return data;
}

export async function get<T>(endpoint: string, serviceKey?: string, opts?: RequestOpts): Promise<T> {
  return request<T>(endpoint, { method: 'GET' }, false, serviceKey, opts);
}

export async function getById<T>(endpoint: string, id: string, queryParams?: string, serviceKey?: string): Promise<T> {
  const url = queryParams ? `${endpoint}/${id}?${queryParams}` : `${endpoint}/${id}`;
  return request<T>(url, { method: 'GET' }, false, serviceKey);
}

export async function post<T>(endpoint: string, body: unknown, serviceKey?: string, signal?: AbortSignal): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
  }, false, serviceKey);
}

export async function put<T>(endpoint: string, body: unknown, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  }, false, serviceKey);
}

export async function patch<T>(endpoint: string, body: unknown, serviceKey?: string, opts?: RequestOpts): Promise<T> {
  return request<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }, false, serviceKey, opts);
}

export async function del<T>(
  endpoint: string,
  serviceKey?: string,
  options?: { keepalive?: boolean },
): Promise<T> {
  return request<T>(
    endpoint,
    { method: 'DELETE', ...(options?.keepalive ? { keepalive: true } : {}) },
    false,
    serviceKey,
  );
}

export async function postFormData<T>(endpoint: string, formData: FormData, isRetry = false): Promise<T> {
  const url = withLangParam(`${API_BASE_URL}${endpoint}`);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(false, undefined, 'POST'),
      ...(isCookieMode ? { credentials: 'include' as RequestCredentials } : {}),
      body: formData,
    });
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') {
      handleNetworkFailure();
    }
    throw err;
  }

  if (!response.ok) {
    // See request() for the CSRF + 401 recovery rationale — same logic, just
    // mirrored here because postFormData bypasses request() for multipart bodies.
    if (
      response.status === 403 &&
      !isRetry &&
      !endpoint.includes('/auth/refresh') &&
      !endpoint.includes('/auth/logout')
    ) {
      const peek = await response.clone().text();
      let isCsrfInvalid = false;
      try {
        isCsrfInvalid = JSON.parse(peek)?.error?.code === 'CSRF_INVALID';
      } catch { /* not JSON */ }

      if (isCsrfInvalid) {
        const refreshed = await attemptTokenRefresh();
        if (refreshed === 'ok') {
          return postFormData<T>(endpoint, formData, true);
        }
        if (refreshed === 'no_refresh') {
          await triggerAuthFailureSoftLogout();
        } else {
          await triggerAuthFailureLogout();
        }
        throw new Error(`API Error 403: CSRF_INVALID, refresh failed`);
      }
    }

    if (response.status === 401 && !isRetry) {
      // Same always-try-refresh policy as request() — the refresh result is
      // the authoritative signal, not the original 401 code. See request()
      // for the full rationale.
      const refreshed = await attemptTokenRefresh();
      if (refreshed === 'ok') {
        return postFormData<T>(endpoint, formData, true);
      }
      if (useAuthStore.getState().isAuthenticated) {
        if (refreshed === 'no_refresh') {
          await triggerAuthFailureSoftLogout();
        } else {
          await triggerAuthFailureLogout();
        }
        throw new Error(`API Error 401: refresh failed`);
      }
    }

    const errorBody = await response.text();

    let correlationId: string | undefined;
    let userMessage: string | undefined;
    let errorCode: string | undefined;
    try {
      const errorJson = JSON.parse(errorBody);
      correlationId = errorJson.correlationId || errorJson.CorrelationId;
      errorCode = errorJson?.error?.code;
      userMessage =
        translateBackendErrorCode(errorJson) ??
        (typeof errorJson.message === 'string' ? errorJson.message : undefined);
    } catch {
      // Not JSON or no correlationId
    }

    const error = new Error(`API Error ${response.status}: ${errorBody}`);
    // Recursion guard — see the matching block in request() for rationale.
    // Nothing calls postFormData against /log today, but keeping the guard
    // symmetric costs nothing and stops a future caller adding a loop.
    if (endpoint !== '/log') {
      logError(error, `POST ${endpoint} failed`, undefined, {
        status: response.status,
        endpoint,
        body: errorBody,
        correlationId,
      });
    } else {
      console.error(`POST ${endpoint} failed`, response.status, errorBody);
    }

    if (response.status === 401) {
      // No-op: see matching block in request(). 401s never show the modal here.
    } else {
      maybeShowErrorModal(userMessage ?? error.message, correlationId, errorCode);
    }

    throw error;
  }

  if (response.status === 204) {
    logTrace(`POST ${endpoint} completed`, undefined, { status: 204 });
    return undefined as T;
  }

  const data = await response.json();
  logTrace(`POST ${endpoint} completed`, undefined, { status: response.status, data });
  return data;
}
